import React, { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDown } from '@videoblocks/react-icons';

import { slugify } from '../../../common/utils';
import BusinessMenu from '../components/BusinessMenu';
import CreativeToolsMenu from '../components/CreativeToolsMenu';
import ResourcesMenu from '../components/ResourcesMenu';
import StockMediaMenu from '../components/StockMediaMenu';

enum DropdownIds {
  StockMedia = 'content',
  CreativeTools = 'creative-tools',
  Businesses = 'businesses',
  Resources = 'resources',
}

type Dropdown = {
  id: DropdownIds;
  title: string;
  menu: React.ReactElement;
};

const NAV_DROPDOWNS: Dropdown[] = [
  {
    id: DropdownIds.StockMedia,
    title: 'Stock Media',
    menu: <StockMediaMenu />,
  },
  {
    id: DropdownIds.CreativeTools,
    title: 'Creative Tools',
    menu: <CreativeToolsMenu />,
  },
  {
    id: DropdownIds.Businesses,
    title: 'Businesses',
    menu: <BusinessMenu />,
  },
  {
    id: DropdownIds.Resources,
    title: 'Resources',
    menu: <ResourcesMenu />,
  },
];

const navClasses =
  'h-20 group relative inline-flex items-center xl:px-4 px-2 md:px-1';

const MainNavContainer = () => {
  const [openDropdownId, setOpenDropdownId] = useState<DropdownIds | null>(
    null
  );

  const toggleDropdown = (id: DropdownIds) => {
    setOpenDropdownId((prev) => (prev === id ? null : id));
  };
  const openDropdown = (dropdownId: DropdownIds) => {
    setOpenDropdownId(dropdownId);
  };
  const closeDropdown = () => {
    setOpenDropdownId(null);
  };

  return (
    <div className="mainNav-siteLinks flex justify-evenly w-full">
      {NAV_DROPDOWNS.map((dropdown) => (
        <div
          className={twMerge(
            `mainNav-${dropdown.id}`,
            openDropdownId === dropdown.id ? 'bg-blue-100' : '',
            navClasses
          )}
          key={dropdown.id}
          aria-expanded={openDropdownId === dropdown.id}
          role="combobox"
          tabIndex={0}
          aria-label={dropdown.title}
          onMouseOver={() => openDropdown(dropdown.id)}
          onMouseLeave={() => closeDropdown()}
          onFocus={() => openDropdown(dropdown.id)}
          onBlur={(e) => {
            if (e.currentTarget && !e.currentTarget.contains(e.relatedTarget)) {
              closeDropdown();
            }
          }}
          onKeyDown={(evt) => {
            if (evt.key === 'Enter' || evt.key === ' ') {
              evt.preventDefault();
              toggleDropdown(dropdown.id);
            } else if (evt.key === 'Escape') {
              evt.currentTarget.focus();
              closeDropdown();
            }
          }}
          id={`main-nav-${slugify(dropdown.title)}`}
          aria-controls={`${slugify(dropdown.title)}Dropdown`}
        >
          <span className="flex items-center font-semibold text-gray-900">
            {dropdown.title}
            <ChevronDown
              className={twMerge(
                'w-2 h-2 ml-2 transition-transform transform fill-black',
                openDropdownId === dropdown.id ? 'rotate-180' : ''
              )}
            />
          </span>
          <div
            className={twMerge(
              `${dropdown.id}-menu`,
              'shadow-popover absolute leading-6 bg-white rounded-xl z-10 pr-px pt-2',
              'top-20 storyboard-lg:-left-3/4 -left-12',
              dropdown.id === DropdownIds.StockMedia ? 'w-288' : 'w-72',
              openDropdownId === dropdown.id ? 'visible' : 'invisible'
            )}
            id={`${slugify(dropdown.title)}Dropdown`}
          >
            {dropdown.menu}
          </div>
          <div
            className={twMerge(
              'bottomBorder bg-blue-900 w-full left-0 bottom-1 xl:bottom-0 absolute',
              openDropdownId === dropdown.id ? 'visible' : 'invisible'
            )}
          />
        </div>
      ))}
    </div>
  );
};

export default MainNavContainer;
